import { useSelector } from 'react-redux';
import useSWR, { mutate } from 'swr';
import { cache } from 'swr/_internal';
import useSWRImmutable from 'swr/immutable';
import useSWRInfinite from 'swr/infinite';
import useSWRMutation from 'swr/mutation';
import {
    assignFieldsToGrowerFromAPI,
    deleteFieldFromAPI,
    getFieldFromAPI,
    getFieldsByProductFromAPI,
    getFieldsFromAPI,
    getFieldViewFieldsFromAPI,
    importFieldViewFieldsFromAPI,
    updateFieldFromAPI,
} from '/@/store/fields/apiProviders';
import { getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { CropType } from '/@/types/crop-monitoring';

export const useMyFields = (
    {
        page,
        pageSize = 10,
        name,
        country,
        geometryType,
        ordering,
        selectOnly,
        userNodeId,
        includeChildren = true,
        excludeTargetNode = false,
        cmCornPhenologyStage,
        cmPlantingDateFrom,
        cmPlantingDateTo,
        campaign,
        crop,
        activeCrop,
        active,
        idIn,
        nodeType,
    }: {
        page: number;
        pageSize?: number;
        name?: string;
        country?: string;
        geometryType?: 'point' | 'polygon';
        ordering?:
            | 'name'
            | '-name'
            | 'location'
            | '-location'
            | 'planting_date'
            | '-planting_date'
            | 'harvest_date'
            | '-harvest_date';
        selectOnly?: string;
        userNodeId?: number;
        includeChildren?: boolean;
        excludeTargetNode?: boolean;
        cmCornPhenologyStage?: string;
        cmPlantingDateFrom?: string;
        cmPlantingDateTo?: string;
        campaign?: number;
        crop?: CropType;
        activeCrop?: CropType | 'fallow';
        active?: boolean;
        idIn?: string;
        nodeType?: string;
    },
    options: { enabled?: boolean } = { enabled: true }
) => {
    return useSWR(
        options.enabled
            ? {
                  key: 'useMyFields',
                  page,
                  pageSize,
                  name,
                  country,
                  geometryType,
                  ordering,
                  selectOnly,
                  userNodeId,
                  includeChildren,
                  excludeTargetNode,
                  cmCornPhenologyStage,
                  cmPlantingDateFrom,
                  cmPlantingDateTo,
                  campaign,
                  crop,
                  activeCrop,
                  active,
                  idIn,
                  nodeType,
              }
            : null,
        getFieldsFromAPI,
        {
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            keepPreviousData: true,
        }
    );
};
const getKey =
    ({
        name,
        country,
        pageSize,
        geometryType,
        ordering,
        selectOnly,
        userNodeId,
        includeChildren,
        cmCornPhenologyStage,
        cmPlantingDateFrom,
        cmPlantingDateTo,
        crop,
        activeCrop,
        excludeTargetNode,
        nodeType,
    }: {
        name: string;
        country: string;
        pageSize: number;
        geometryType?: 'point' | 'polygon';
        ordering?: 'name' | '-name' | 'location' | '-location';
        selectOnly?: string;
        userNodeId?: number;
        includeChildren?: boolean;
        cmCornPhenologyStage?: string;
        cmPlantingDateFrom?: string;
        cmPlantingDateTo?: string;
        crop?: CropType;
        activeCrop?: CropType | 'fallow';
        excludeTargetNode?: boolean;
        nodeType?: string;
    }) =>
    (pageIndex: number, previousPageData: any) => {
        if (previousPageData && pageSize * pageIndex >= previousPageData.count) return null;
        return {
            key: 'useMyFieldsInfinite',
            page: pageIndex + 1,
            name,
            country,
            pageSize,
            geometryType,
            ordering,
            selectOnly,
            userNodeId,
            includeChildren,
            cmCornPhenologyStage,
            cmPlantingDateFrom,
            cmPlantingDateTo,
            crop,
            activeCrop,
            excludeTargetNode,
            nodeType,
        };
    };

const updateCache = (currentData, fieldId, fieldPayload = undefined) => {
    try {
        const isInfinite = Array.isArray(currentData) && currentData?.[0].results !== undefined;
        const isPaginated = currentData?.results !== undefined;
        if (isPaginated) currentData = [currentData]; // Hack to convert to Infinite

        if (!isInfinite && !isPaginated && currentData?.id !== undefined) {
            // Single data
            return fieldPayload;
        }

        let newData;
        if (fieldPayload) {
            newData = currentData.map((row) => ({
                ...row,
                results: row.results.map((field) => {
                    if (field.id === fieldId) {
                        // Update Field
                        return { ...field, ...fieldPayload };
                    }
                    return field;
                }),
            }));
        }
        if (isPaginated) return newData?.[0];
        if (isInfinite) return newData;
    } catch (error) {
        console.log(error);
    }
};

export const revalidateFieldViewFields = () => {
    for (const key of cache.keys()) {
        if (key.includes('key:"useFieldViewFields"')) {
            setTimeout(() => mutate(key, undefined, true));
        }
    }
};

export const revalidateMyFields = ({
    country,
    fieldId,
    fieldPayload,
}: {
    country: string;
    fieldId?: number;
    fieldPayload?: unknown;
}) => {
    for (const key of cache.keys()) {
        if (
            ((key.includes('key:"useMyFields"') || key.includes('key:"useMyFieldsInfinite"')) &&
                key.includes(`country:"${country}"`)) ||
            (key.includes('key:"fieldById"') && key.includes(`fieldId:"${fieldId}"`))
        ) {
            mutate(
                key,
                (currentData) => {
                    if (!fieldId) return;
                    const updatedCache = updateCache(currentData, fieldId, fieldPayload);
                    return updatedCache;
                },
                true
            );
            setTimeout(() => mutate(key, undefined, true)); // HACK FOR CACHE INVALIDATE
        }

        const fieldsDashboard = key.includes('key:"fieldsDashboard"');
        const growersDashboard = key.includes('key:"growersDashboard"');
        const recommendationsDashboard = key.includes('key:"recommendationsDashboard"');
        const useGrowers = key.includes('key:"useGrowers"');
        if (fieldsDashboard || growersDashboard || recommendationsDashboard || useGrowers) {
            setTimeout(() => mutate(key, undefined, true));
        }
    }
};

export const useMyFieldsInfinite = ({
    name,
    country,
    pageSize = 10,
    geometryType,
    ordering,
    selectOnly,
    userNodeId,
    includeChildren = true,
    excludeTargetNode = false,
    cmCornPhenologyStage,
    cmPlantingDateFrom,
    cmPlantingDateTo,
    crop,
    activeCrop,
    nodeType,
}: {
    name: string;
    country: string;
    pageSize?: number;
    geometryType?: 'point' | 'polygon';
    ordering?: 'name' | '-name' | 'location' | '-location';
    selectOnly?: string;
    userNodeId?: number;
    includeChildren?: boolean;
    excludeTargetNode?: boolean;
    cmCornPhenologyStage?: string;
    cmPlantingDateFrom?: string;
    cmPlantingDateTo?: string;
    crop?: CropType;
    activeCrop?: CropType | 'fallow';
    nodeType?: string;
}) =>
    useSWRInfinite(
        getKey({
            name,
            country,
            pageSize,
            geometryType,
            ordering,
            selectOnly,
            userNodeId,
            includeChildren,
            excludeTargetNode,
            cmCornPhenologyStage,
            cmPlantingDateFrom,
            cmPlantingDateTo,
            crop,
            activeCrop,
            nodeType,
        }),
        getFieldsFromAPI,
        {
            revalidateIfStale: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            revalidateFirstPage: false,
            revalidateAll: false,
            keepPreviousData: true,
        }
    );

export const useFieldById = ({ id }: { id: number }, options: { enabled?: boolean } = { enabled: true }) => {
    let key = { key: 'useFieldById', fieldId: id };
    if (!options.enabled || !id) key = null;

    return useSWR(key, getFieldFromAPI, {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        keepPreviousData: true,
    });
};

/**
 * Fetch FieldView fields (from FieldView API v5) for the current user.
 * Optionally scope by fieldview_id; when omitted, returns fields from all linked accounts.
 * Use debug: true to include _timing_ms in the response.
 */
export const useFieldViewFields = (
    {
        fieldviewId,
    }: {
        fieldviewId?: number;
    } = {},
    options: { enabled?: boolean } = { enabled: true }
) => {
    const key = options.enabled ? { key: 'useFieldViewFields', fieldviewId: fieldviewId } : null;

    return useSWR(
        key,
        (k) =>
            getFieldViewFieldsFromAPI({
                fieldviewId: k?.fieldviewId,
            }),
        {
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            keepPreviousData: true,
        }
    );
};

export const useImportFieldViewFieldsMutation = () => {
    const country = useSelector(getSessionCountry);
    return useSWRMutation(
        { key: 'importFieldViewFields' },
        (
            _key,
            {
                arg,
            }: {
                arg: { fieldviewId: string | number; fieldviewFieldIds: string[] };
            }
        ) =>
            importFieldViewFieldsFromAPI({
                fieldviewId: arg.fieldviewId,
                fieldviewFieldIds: arg.fieldviewFieldIds,
            }),
        {
            onSuccess: (_data, _key, _config) => {
                if (country?.code) {
                    revalidateMyFields({ country: country.code });
                }
                revalidateFieldViewFields();
            },
        }
    );
};

export const useMyFieldsByProduct = (
    {
        page = 1,
        pageSize = 10000,
        country,
        product_id,
        climate_pattern,
        planting_date,
        soil_moisture,
        userNodeId,
        includeChildren = true,
        excludeTargetNode,
    }: {
        page: number;
        pageSize?: number;
        country?: string;
        product_id?: number;
        climate_pattern: string;
        planting_date: string;
        soil_moisture?: string;
        userNodeId?: number;
        includeChildren?: boolean;
        excludeTargetNode?: boolean;
    },
    options: { enabled?: boolean } = { enabled: true }
) => {
    let key: {
        key: string;
        page: number;
        pageSize: number;
        country?: string;
        product_id?: number;
        climate_pattern: string;
        planting_date: string;
        soil_moisture?: string;
        userNodeId?: number;
        includeChildren: boolean;
        excludeTargetNode?: boolean;
    } | null = {
        key: 'useMyFieldsByProduct',
        page,
        pageSize,
        country,
        product_id,
        climate_pattern,
        planting_date,
        soil_moisture,
        userNodeId,
        includeChildren,
        excludeTargetNode,
    };
    if (!options.enabled || !product_id || !climate_pattern || !planting_date) key = null;
    return useSWRImmutable(
        key,
        ({
            page,
            pageSize,
            country,
            product_id,
            climate_pattern,
            planting_date,
            soil_moisture,
            userNodeId,
            includeChildren,
            excludeTargetNode,
        }) =>
            getFieldsByProductFromAPI({
                page,
                pageSize,
                country,
                product_id,
                climate_pattern,
                planting_date,
                soil_moisture,
                userNodeId,
                includeChildren,
                excludeTargetNode,
            })
    );
};

export const useUpdateFieldMutation = (fieldId: number) => {
    const country = useSelector(getSessionCountry);
    return useSWRMutation(
        {
            key: 'updateField',
            fieldId,
        },
        (
            key,
            {
                arg,
            }: {
                arg: { name?: string; area?: number; enabledSendFrostRiskNotification?: boolean; userNodeId?: number };
            }
        ) =>
            updateFieldFromAPI({
                fieldId: key.fieldId,
                name: arg.name,
                area: arg.area,
                userNodeId: arg.userNodeId,
                country: country.code,
                enabledSendFrostRiskNotification: arg.enabledSendFrostRiskNotification,
            }),
        {
            onSuccess: () => {
                mpTrackEvent('Field edited', undefined);
            },
        }
    );
};

export const useAssignFieldsToGrowerMutation = () => {
    const country = useSelector(getSessionCountry);
    return useSWRMutation(
        { key: 'assignFieldsToGrower' },
        (
            _key,
            {
                arg,
            }: {
                arg: { fieldIds: number[]; userNodeId: number };
            }
        ) =>
            assignFieldsToGrowerFromAPI({
                fieldIds: arg.fieldIds,
                userNodeId: arg.userNodeId,
                country: country.code,
            }),
        {
            onSuccess: () => {
                mpTrackEvent('Field edited', undefined);
            },
        }
    );
};

export const useDeleteFieldMutation = (fieldId: number) => {
    const country = useSelector(getSessionCountry);
    return useSWRMutation(
        {
            key: 'deleteField',
            fieldId,
        },
        (key) => deleteFieldFromAPI({ fieldId: key.fieldId, country: country.code })
    );
};
